import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { ResponseErrorService } from '../errors/response-error.service';

import { WorkspaceApiProviderService } from '@core/api';
import { WindowService } from '@core/services';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { PromptService } from '../../components/prompt/prompt.service';
import { CustomFieldsApiProviderService } from '../../data-providers/api-providers/workspace-api-provider/custom-fields-api-provider.service';
import { TNewCustomField } from '../../data-providers/api-providers/workspace-api-provider/workspace-requests.model';
import { TranslationPipe } from '../../features/translate/translation.pipe';
import { getErrorMessage } from '../../helpers/database/get-error-message';
import { TSiteResponse } from '../workspace/site-response.model';
import { setChangedWorkspace } from '../workspace/workspace';
import { ECustomFieldType } from './custom-field-types-enums';
import {
  TAllCustomFields,
  TCustomField,
  TCustomFieldList,
  TWorkspaceCustomFields,
} from './custom-fields.model';
import { SET_CUSTOM_FIELDS } from './custom-fields.store';

export enum ECustomFieldsEventType {
  SAVE_CUSTOM_FIELD_CLICKED,
  ALPHABETIZE,
  ALL_SORT,
  FIELD_CHANGES,
  LEVEL_CHANGE,
}

type ISorted = {
  sorted: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class CustomFieldsService implements OnDestroy {
  private _saveCustomFieldClicked$ = new Subject<void>();
  private _alphabetize$ = new Subject<boolean>();
  private _allSort$ = new Subject<void>();
  private _fieldChanges$ = new Subject<void>();
  private _depthChange$ = new Subject<void>();
  private _maxListDepthChange$ = new BehaviorSubject<number>(3);

  saveCustomFieldClicked$ = this._saveCustomFieldClicked$.asObservable();
  alphabetize$ = this._alphabetize$.asObservable();
  allSort$ = this._allSort$.asObservable();
  fieldChanges$ = this._fieldChanges$.asObservable();
  depthChange$ = this._depthChange$.asObservable();
  maxListDepthChange$ = this._maxListDepthChange$.asObservable();

  private allSorted: ISorted = {
    sorted: false,
  };

  private readonly destroy$ = new Subject<void>();

  private customFields: TAllCustomFields = {};

  constructor(
    private responseErrorService: ResponseErrorService,
    private customFieldsApiProviderService: CustomFieldsApiProviderService,
    private workspaceApiProviderService: WorkspaceApiProviderService,
    private windowService: WindowService,
    private translationPipe: TranslationPipe,
    private promptService: PromptService,
  ) {
    this.windowService.resize$.pipe(takeUntil(this.destroy$)).subscribe(({ innerWidth }) => {
      this.onResize(innerWidth);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  getCustomFields(): TAllCustomFields {
    return this.customFields;
  }

  setCustomFields(customFields: TAllCustomFields): TAllCustomFields {
    this.customFields = customFields;
    SET_CUSTOM_FIELDS(this.customFields);

    return this.customFields;
  }

  getWorkspaceCustomFields(workspaceId: string): TWorkspaceCustomFields {
    return this.customFields[workspaceId];
  }

  getWorkspaceCustomField(workspaceId: string, customFieldId: string): TCustomField {
    return this.customFields[workspaceId]?.[customFieldId]; // ?. is used because with asset management it's now possible to get CF ID for workspace user can't access
  }

  setWorkspaceCustomFields(workspaceId: string, customFields: TWorkspaceCustomFields): void {
    this.customFields[workspaceId] = customFields;

    SET_CUSTOM_FIELDS(this.customFields);
  }

  clearCustomFields(): TAllCustomFields {
    this.customFields = {};
    SET_CUSTOM_FIELDS(this.customFields);

    return this.getCustomFields();
  }

  addWorkspaceCustomField(workspaceId: string, customField: TCustomField): void {
    if (!this.customFields[workspaceId]) {
      this.customFields[workspaceId] = {};
    }

    this.customFields[workspaceId][customField.id.toString()] = customField;

    SET_CUSTOM_FIELDS(this.customFields);
  }

  updateWorkspaceCustomField(workspaceId: string, customField: TCustomField): void {
    this.customFields[workspaceId][customField.id.toString()] = customField;

    setChangedWorkspace(workspaceId);

    SET_CUSTOM_FIELDS(this.customFields);
  }

  updateWorkspaceCustomFieldVisibility(
    workspaceId: string,
    customFieldId: string,
    display: boolean,
  ): void {
    this.customFields[workspaceId][customFieldId].display = display;

    setChangedWorkspace(workspaceId);

    SET_CUSTOM_FIELDS(this.customFields);
  }

  updateWorkspaceCustomFieldLocked(
    workspaceId: string,
    customFieldId: string,
    lockedValue: boolean,
  ): void {
    this.customFields[workspaceId][customFieldId].lockedValue = lockedValue;

    setChangedWorkspace(workspaceId);

    SET_CUSTOM_FIELDS(this.customFields);
  }

  addCustomField(workspaceId: string, customField: TNewCustomField): Observable<TCustomField> {
    return this.workspaceApiProviderService
      .addCustomField(workspaceId, customField)
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to add custom field'),
        ),
      );
  }

  updateCustomField(
    workspaceId: string,
    customField: Partial<TCustomField>,
  ): Observable<TCustomField[]> {
    return this.workspaceApiProviderService
      .updateCustomField(workspaceId, customField)
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to update custom field'),
        ),
      );
  }

  deleteCustomField(workspaceId: string, customFieldId: string): Observable<null> {
    return this.workspaceApiProviderService.deleteCustomField(workspaceId, customFieldId).pipe(
      tap(() => {
        delete this.customFields[workspaceId][customFieldId];

        SET_CUSTOM_FIELDS(this.customFields);
      }),
      catchError((error) =>
        this.responseErrorService.handleRequestError(error, 'Failed to delete custom field'),
      ),
    );
  }

  lockCustomField(workspaceId: string, customFieldId: string): Observable<void> {
    return this.workspaceApiProviderService.lockCustomField(workspaceId, customFieldId).pipe(
      tap(() => {
        const prompt = this.translationPipe.transform('custom_field_editability_change_success');

        this.promptService.showSuccess(prompt);

        this.customFields[workspaceId][customFieldId].lockedValue = true;

        setChangedWorkspace(workspaceId);

        SET_CUSTOM_FIELDS(this.customFields);
      }),
      catchError((error) => {
        const prompt = this.translationPipe.transform('custom_field_editability_change_error');
        this.promptService.showError(prompt);

        return this.responseErrorService.handleRequestError(error, 'Failed to lock custom field');
      }),
    );
  }

  unlockCustomField(workspaceId: string, customFieldId: string): Observable<void> {
    return this.workspaceApiProviderService.unlockCustomField(workspaceId, customFieldId).pipe(
      tap(() => {
        const prompt = this.translationPipe.transform('custom_field_editability_change_success');

        this.promptService.showSuccess(prompt);

        this.customFields[workspaceId][customFieldId].lockedValue = false;

        setChangedWorkspace(workspaceId);

        SET_CUSTOM_FIELDS(this.customFields);
      }),
      catchError((error) => {
        const prompt = this.translationPipe.transform('custom_field_editability_change_error');
        this.promptService.showError(prompt);

        return this.responseErrorService.handleRequestError(error, 'Failed to unlock custom field');
      }),
    );
  }

  importListCustomField(
    workspaceId: string,
    excel: File,
    listType: ECustomFieldType,
  ): Observable<TCustomField> {
    const formData = new FormData();

    formData.append('the_file', excel);

    return this.customFieldsApiProviderService
      .importListCustomFieldFromExcel(workspaceId, formData, listType)
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to import list custom field'),
        ),
      );
  }

  reorderCustomFields(
    workspaceId: string,
    orderedCustomFieldIds: string[],
  ): Observable<TSiteResponse> {
    return this.workspaceApiProviderService
      .reorderCustomFields(workspaceId, orderedCustomFieldIds)
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to reorder custom fields'),
        ),
      );
  }

  setAllSorted(allSorted: boolean): void {
    this.allSorted.sorted = allSorted;
  }

  // TODO Refactor sorting logic. Avoid using object link
  getAllSorted(): ISorted {
    return this.allSorted;
  }

  onResize(innerWidth: number): void {
    let level = 1;

    if (innerWidth > 600) {
      level = 3;
    } else if (innerWidth > 500) {
      level = 2;
    }

    if (level !== this._maxListDepthChange$.value) {
      this._maxListDepthChange$.next(level);
    }
  }

  generateCustomFieldRequestData(customField: TCustomField): TNewCustomField {
    const oldFieldDataJSON = JSON.stringify(customField);
    const fieldLabel = ',"fieldError":false';
    const newFieldDataJSON = oldFieldDataJSON.split(fieldLabel).join('');
    const newFieldData = JSON.parse(newFieldDataJSON);

    return this.generateNewCFBody(customField, newFieldData);
  }

  emit(event: ECustomFieldsEventType, emittedObject?: boolean): void {
    switch (event) {
      case ECustomFieldsEventType.ALL_SORT:
        this._allSort$.next();
        break;
      case ECustomFieldsEventType.ALPHABETIZE:
        this._alphabetize$.next(emittedObject);
        break;
      case ECustomFieldsEventType.FIELD_CHANGES:
        this._fieldChanges$.next();
        break;
      case ECustomFieldsEventType.LEVEL_CHANGE:
        this._depthChange$.next();
        break;
      case ECustomFieldsEventType.SAVE_CUSTOM_FIELD_CLICKED:
        this._saveCustomFieldClicked$.next();
        break;
    }
  }

  getCustomFieldListDepth(fieldList: TCustomFieldList[]): number {
    return (
      1 +
      Math.max(
        0,
        ...fieldList.map((field) => {
          if (field.subList && field.subList.length) {
            this.getCustomFieldListDepth(field.subList);
          }
          return 0;
        }),
      )
    );
  }

  private generateNewCFBody(
    customField: TCustomField,
    newFieldData: TCustomField,
  ): TNewCustomField {
    const commonFields: TNewCustomField = {
      label: newFieldData.label,
      type: newFieldData.type,
      description: newFieldData.description || '',
      groupId: newFieldData?.groupId || '',
    };

    switch (customField.type) {
      case ECustomFieldType.TEXT:
      case ECustomFieldType.RICHTEXT:
      case ECustomFieldType.CHECKBOX:
      case ECustomFieldType.TIMELINE:
      case ECustomFieldType.DATE:
      case ECustomFieldType.SIGNATURE:
        return commonFields;
      case ECustomFieldType.LIST:
      case ECustomFieldType.MULTI_LIST:
        return {
          ...commonFields,
          subList: newFieldData.subList,
        };
      case ECustomFieldType.TIME:
        return {
          ...commonFields,
          showTotal: newFieldData.showTotal,
          showHoursOnly: newFieldData.showHoursOnly,
        };
      case ECustomFieldType.COST:
        return {
          ...commonFields,
          showTotal: newFieldData.showTotal,
          currencyCode: newFieldData.currencyCode,
          decimalPlaces: '2',
          subValuesActive: newFieldData.subValuesActive,
        };
      case ECustomFieldType.NUMBERS:
        return {
          ...commonFields,
          showTotal: newFieldData.showTotal,
          showCommas: newFieldData.showCommas,
          decimalPlaces: newFieldData.decimalPlaces.toString(),
          unit: newFieldData.unit,
          subValuesActive: newFieldData.subValuesActive,
        };
      case ECustomFieldType.PERCENTAGE:
        return {
          ...commonFields,
          decimalPlaces: '0',
          subValuesActive: newFieldData.subValuesActive,
        };
      case ECustomFieldType.FORMULA:
        switch (newFieldData.outputType) {
          case ECustomFieldType.COST:
            return {
              ...commonFields,
              showTotal: newFieldData.showTotal,
              currencyCode: newFieldData.currencyCode,
              formula: newFieldData.formula,
              outputType: newFieldData.outputType,
              decimalPlaces: '2',
            };
          case ECustomFieldType.NUMBERS:
            return {
              ...commonFields,
              showTotal: newFieldData.showTotal,
              showCommas: newFieldData.showCommas,
              decimalPlaces: newFieldData.decimalPlaces
                ? newFieldData.decimalPlaces.toString()
                : '0',
              unit: newFieldData.unit,
              formula: newFieldData.formula,
              outputType: newFieldData.outputType,
            };
          case ECustomFieldType.PERCENTAGE:
            return {
              ...commonFields,
              decimalPlaces: '0',
              formula: newFieldData.formula,
              outputType: newFieldData.outputType,
            };
        }

        break;
      default:
        throw new Error(`Unhandled custom field type: ${customField.type}`);
    }
  }

  changeCustomFieldVisibility(
    workspaceId: string,
    customFieldId: string,
    display: boolean,
  ): Observable<void> {
    return this.workspaceApiProviderService
      .toggleCustomFieldDisplay(workspaceId, customFieldId, display)
      .pipe(
        tap(() => {
          const prompt = this.translationPipe.transform('custom_field_visibility_change_success');

          this.promptService.showSuccess(prompt);

          this.updateWorkspaceCustomFieldVisibility(workspaceId, customFieldId, display);
        }),
        catchError((error) => {
          switch (error.status) {
            case EStatusCode.BAD_REQUEST:
            case EStatusCode.FORBIDDEN:
              getErrorMessage(error).then((message) => {
                this.promptService.showError(message);
              });
              break;
            default: {
              const prompt = this.translationPipe.transform('custom_field_visibility_change_error');
              this.promptService.showError(prompt);
              break;
            }
          }

          return this.responseErrorService.handleRequestError(
            error,
            'Failed to change custom field visibility',
          );
        }),
      );
  }
}
